import type { CaptureContext, ScopeContext } from '@sentry/types'
import { createContext } from 'react'

export const ErrorHandlingContext = createContext<ErrorHandlingProvider>({
  unexpected: () => {},
  expected: () => {},
  data_provider: () => {},
  http: () => {},
  long_process: () => {},
})

export type ErrorHandlingProvider = {
  /**
   * Use this handler to capture expected error.
   */
  expected: (err: Error, opts?: ErrorHandlerOpts) => void
  /**
   * Use this handler to capture unexpected logic errors.
   */
  unexpected: (err: Error, opts?: ErrorHandlerOpts) => void // Intentionally narrower error type
  /**
   * Use this handler to capture network/http errors to official OSAPI endpoints.
   */
  data_provider: (err: LooseError, opts?: ErrorHandlerOpts) => void
  /**
   * Use this handler to capture network/http errors to external/unofficial endpoints.
   */
  http: (err: LooseError, opts?: ErrorHandlerOpts) => void
  /**
   * Use this handler to capture long running, sequential processes, that fail to complete for unknown reasons.
   */
  long_process: (err: LooseError, opts?: ErrorHandlerOpts) => void
}

export type ErrorType = keyof ErrorHandlingProvider

export type ErrorHandlerOverrides = {
  before?: { [key in ErrorType]?: (err: LooseError, opts?: ErrorHandlerOpts) => void }
  after?: { [key in ErrorType]?: (err: LooseError, opts?: ErrorHandlerOpts) => void }
  replace?: { [key in ErrorType]?: (err: LooseError, opts?: ErrorHandlerOpts) => void }
}

export type ErrorHandlerOpts = {
  msg?: string
  notify?: boolean
  console?: boolean
  captureContext?: CaptureContext
  scopeContext?: ScopeContext
  getCaptureContext?(): CaptureContext
}

export type LooseError = string | Error | ErrorEvent
